import cx from 'classnames'
import _ from '@/utils/lodash-optimized'
import { useCallback, useState, useMemo } from 'react'
import toast from 'react-hot-toast'
import compiler from 'shared_src/igao/compiler'

import { PlainButton } from '../../../shared/Button'
import * as Card from '../../../shared/card'
import {
  Button,
  DraftButton,
  PortedModal
} from '../../../shared/NarusMaterial'
import { IGAOContext } from '../../context'
import {
  cancelledOrCompleted,
  capitalize,
  detailsFields,
  favorable,
  filterItemsByStatus,
  findById,
  findEqualIssue, generateParamsString,
  getDetail,
  staticType,
  uid,
  initialFiltersDataCP,
  initialFiltersData
} from '../../utils'
import { BatchView } from '../batch-view'
import { CommentPanel } from '../comments-panel'
import { Header } from '../header'
import { RenderItems } from '../render-items'
import { Warning } from '../warnings/warning'
import { Warnings } from '../warnings/warnings'

import styles from './dynamic-igao.module.scss'

export const DynamicIGAO = ({
  data,
  setPageData,
  page,
  draft,
  publish,
  discard,
  resolve,
  readComments,
  disabled,
  error
}) => {
  const [ filters, setFilters ] = useState((page === 'careplan' || page === 'tasks') ? initialFiltersDataCP : initialFiltersData)
  let [ open, setOpen ] = useState(true)
  const [ sort, setSort ] = useState([{
    key: 'patient',
    name: 'Patient Name',
    align: 'center',
    value: 0
  }, {
    key: 'description',
    name: 'Description',
    value: 0
  }, {
    key: 'assignee',
    name: 'Assignee',
    align: 'center',
    value: 0
  }, {
    key: 'status',
    name: 'Status',
    align: 'center',
    value: 0
  }, {
    key: 'dueDays',
    name: 'Due Days',
    align: 'center',
    value: 0
  }])
  const [ masterBatch, setMasterBatch ] = useState(null)
  const [ switchHead, setSwitchHead ] = useState(false)
  const [ search, setSearch ] = useState('')
  const [ isCheckAll, setCheckAll ] = useState(false)
  const [ compactView, setCompactView ] = useState(false)
  const [ selectedIssueId, setSelectedIssueId ] = useState(null)
  const [ loading, setLoading ] = useState(null)
  const [ batch, setBatch ] = useState([])
  const [ modal, setModal ] = useState(null)

  const templateNewIssue = useCallback(({ item, key, id, listItem }) => {
    const { template } = item
    const relations = listItem ? [{ id, name: listItem.name, key }] : []
    const { lists } = data[page]

    const description = compiler.describe({ template, relations, lists })

    const defaultDetails = [
      { ...data[page].user, key: 'assignee' },
      { ...data[page].lists.status.find(s => s.id === 'in-progress'), key: 'status' },
      { key: 'trend' }]

    return {
      id: uid(),
      draft: true,
      description,
      domain: item.domain,
      details: defaultDetails,
      createdAt: new Date().toISOString(),
      relations,
      comment: []
    }
  }, [ data, page ])

  const handleChecked = ({ id, key, parentId, relId }) => {
    setPageData(prev => {
      const _data = { ...prev }
      const listItem = findById(data[page].lists[key], id)
      const item = findById(_data[page].items, parentId)
      const relation = item?.relations.find(r => r.id === relId)
      const valueIndex = relation?.values.findIndex(v => v?.id === listItem.id || listItem.value)
      const issue = item?.issues?.find(i => i?.relations?.find(r => r.id === id) && !cancelledOrCompleted(i))
      const isNotPublished = issue?.draft

      const newIssue = templateNewIssue({ item, key, id, listItem })

      if (valueIndex !== -1 && issue) {
        if (isNotPublished) {
          relation.values.splice(valueIndex, 1)
          item.issues = item.issues.filter(i => i.id !== issue.id)
        }
      } else {
        if (relation.values.length > 0) relation.values.push(listItem)
        else relation.values = [ listItem ]

        if (item?.issues?.length > 0) item.issues.push(newIssue)
        else item.issues = [ newIssue ]
      }
      return _data
    })
  }

  const handleBatch = (itemId, issueId, actionId) => {
    if (!itemId || !issueId) {
      return
    }

    const item = findById(data[page].items, itemId)
    const issue = findById(item.issues, issueId)
    if (!actionId) {
      setBatch(prev => {
        const activeActions = issue?.actions?.filter(a => !cancelledOrCompleted(a)) || []
        const ids = [ ...activeActions?.map(act => act.id), issueId ]
        return !prev.includes(issueId) ? prev.concat(ids.filter(id => !prev.includes(id))) : prev.filter(id => !ids.includes(id))
      })
    } else {
      setBatch(prev => !prev.includes(actionId) ? prev.concat(actionId) : prev.filter(id => id !== actionId))
    }
  }

  const changeActionsStatus = useCallback((issue, id, name) =>
    issue.actions?.map(act => {
      if (cancelledOrCompleted(act)) return act
      if (getDetail(act).assignee.id === data[page].user.id) {
        const tempAct = { ...act }
        const detail = tempAct.details.find(d => d.key === 'status')
        detail.id = id
        detail.name = name
        return tempAct
      }
      return act
    }) || [], [ data, page ])

  const handleBatchAction = type => {
    if (type === 'assign') {
      function changeAssingee (object) {
        const relationIndex = object.details.findIndex((d) => d.key === 'assignee')

        const oldDetails = object.details.filter((d) => d.key !== 'assignee' && d.key !== 'status')

        if (relationIndex < 0) {
          return object
        }

        return {
          ...object,
          details: [
            ...oldDetails,
            {
              key: 'assignee',
              name: data[page].user.name,
              id: data[page].user.id
            },
            {
              key: 'status',
              name: 'Open',
              id: 'in-progress'
            }
          ]
        }
      }

      setPageData((prev) => ({
        [page]: {
          ...prev[page],
          items: prev[page].items.map(item => {
            return ({
              ...item,
              issues: item?.issues?.map(issue => ({
                ...(batch.includes(issue.id) ? changeAssingee(issue) : issue),
                actions: issue?.actions?.map(action => ({
                  ...(batch.includes(action.id) ? changeAssingee(action) : action)
                }))
              }))
            })
          }, [])
        }
      }))
      return
    }

    setPageData(prev => {
      const _data = { ...prev }
      const { items } = _data[page]
      const changeStatus = item => {
        if (
          !cancelledOrCompleted(item) &&
          (masterBatch || batch.includes(item.id)) &&
          getDetail(item).assignee.id === data[page].user.id
        ) {
          const detail = item.details.find(dv => dv.key === 'status')
          detail.id = type
          detail.name = capitalize(type)
        }
        return item
      }

      if (type === 'discard') {
        setBatch([])
        setMasterBatch(null)
        return _data
      }

      items.map(i =>
        filterItemsByStatus(i.issues, filters)
          .filter(iss => generateParamsString(iss)
            ?.toLowerCase()
            ?.indexOf(search?.toLowerCase() || '') !== -1)
          ?.map(iss => {
            const issTemp = { ...iss }
            if (
              !issTemp.actions?.some(
                act =>
                  getDetail(act).assignee.id !== data[page].user.id &&
                  !cancelledOrCompleted(act)
              )
            ) {
              changeStatus(issTemp)
            }
            return filterItemsByStatus(issTemp?.actions, filters)?.map(act => {
              const actTemp = { ...act }
              return changeStatus(actTemp)
            })
          }))
      setMasterBatch(null)
      setBatch([])
      return _data
    })
  }

  const handleInput = (relationId, key, value, itemId) => {
    const item = findById(data[page].items, itemId)
    if (item) {
      const relation = findById(item.relations, relationId)
      relation.value = value
    }
    setPageData(data)
  }

  const createDescription = useCallback((item, issue) => {
    const { lists } = data[page]
    return {
      ...issue,
      description: compiler.describe({ template: item.template, relations: issue.relations, lists })
    }
  }, [ data, page ])

  const handleInputChange = (key, value, item, issue) => {
    if (item) {
      const tempIssue = { ...issue }
      const relation = tempIssue.relations.find(r => r.key === key)
      relation.value = value
    }
    return issue.relations
  }

  const handleInputSubmit = (_comment, type, id) => {
    setPageData(prev => {
      const _data = { ...prev }
      let item
      if (type === 'action') {
        item = _data[page].items
          .find(it =>
            it.issues?.find(is => is.actions?.find(act => act.id === id)))
          ?.issues?.find(is => is.actions?.find(act => act.id === id))
          ?.actions?.find(act => act.id === id)
      } else {
        item = _data[page].items
          .find(it => it?.issues?.find(is => is.id === id))
          ?.issues?.find(is => is.id === id)
      }

      if (!item.comment) item.comment = []

      const comment = findById(item?.comment, id)
      if (comment) comment.value = _comment
      else {
        item.comment.push({
          id: uid(),
          value: _comment,
          draft: true,
          createdAt: new Date().toISOString(),
          author: data[page].user
        })
      }
      return _data
    })
  }

  const createIssue = useCallback((itemId, issue, callback) => {
    const item = findById(data[page].items, itemId)
    if (!item.issues) item.issues = []
    const prevIssues = item.issues
    const equal = findEqualIssue(issue.relations, item.issues).length > 0
    if (!equal) {
      prevIssues.push(issue)
      callback?.()
    }
    setPageData(data)
  }, [ data, page, setPageData ])

  const deleteIssue = useCallback((itemId, issue) => {
    setPageData(prev => {
      const _data = { ...prev }
      const item = findById(_data[page].items, itemId)
      if (!item.issues) item.issues = []
      const prevIssues = item.issues
      const issueIndex = prevIssues.findIndex(iss => iss.id === issue.id)

      if (item.type === 'static') {
        if (issueIndex !== -1) item.issues.splice(issueIndex, 1)
        item.answer = null
        return _data
      }

      if (item?.relations?.length === 1) {
        const relationIndex = item.relations?.[0]?.values?.findIndex(
          i => i.id === issue.relations[0].id
        )
        item.relations[0].values?.splice(relationIndex, 1)
      }

      item.issues.splice(issueIndex, 1)
      if (item.issues.length <= 1) {
        item.issues =
          item?.issues[0]?.relations?.length !== 0 ? item.issues : []
      }
      return _data
    })
  }, [ page, setPageData ])

  const createAction = useCallback((itemId, issueId, action, add = true) => {
    setPageData(prev => {
      const _data = { ...prev }
      const item = findById(_data[page].items, itemId)
      const issue = findById(item.issues, issueId)
      if (add) {
        if (!issue.actions) {
          issue.actions = [ action ]
        } else {
          issue.actions.push(action)
        }
      } else {
        const issueIndex = item.issues.findIndex(i => i.id === issueId)
        const actionIndex = item.issues[issueIndex].actions.findIndex(
          act => act.id === action.id
        )
        item.issues[issueIndex].actions.splice(actionIndex, 1)
      }
      return _data
    })
  }, [ page, setPageData ])

  const editIssue = useCallback((itemId, issueId, details, changedRelations) => {
    setPageData(prev => {
      const _data = { ...prev }
      const itemIndex = _data[page].items.findIndex(i => i.id === itemId)
      const item = _data[page].items[itemIndex]
      const issue = findById(item.issues, issueId)
      const currentIssueRelations = _.cloneDeep(issue.relations)
      const currentItemRelations = _.cloneDeep(item.relations)
      const { assignee, status, trend: _trend } = getDetail(issue)
      const equalIssueDetail =
        assignee.id === details.assignee &&
        status.id === details.status &&
        issue.description === details.description &&
        _trend?.id === details?.trend
      const relKeys = Object.keys(changedRelations)

      if (relKeys.length <= 0 && equalIssueDetail) return _data

      if (issue.draft) {
        relKeys?.map(key => {
          if (detailsFields.includes(key)) {
            const value = changedRelations[key]
            if (value.length > 0 || key.indexOf('optional') !== -1) {
              issue.relations = handleInputChange(key, value, item, issue)
            }
          } else {
            const relations = item.relations.find(rel => rel.key === key)
            const relationIndex = relations.values.findIndex(
              r => r.id === changedRelations[key].oldValue
            )
            const relationsIndex = issue.relations.findIndex(
              r => r.key === key
            )
            const newRelation = findById(
              _data[page].lists[key],
              changedRelations[key].newValue
            )
            relations.values[relationIndex] = newRelation
            issue.relations[relationsIndex] = {
              ...newRelation,
              key
            }
          }
          return issue
        })

        const equal =
          findEqualIssue(
            issue.relations,
            item.issues.filter(i => i.id !== issueId)
          ).length > 0

        if (equal) {
          issue.relations = currentIssueRelations
          item.relations = currentItemRelations
          toast.error('Can\'t create duplicate issue', { position: 'bottom-center' })
        }

        issue.description = createDescription(item, issue).description
      }

      Object.keys(details)?.map(key => {
        const id = details[key]
        const listItem = _data[page].lists[key]?.find(i => i.id === id)
        if (key === 'status') {
          const isNotActiveStatus = listItem.id === 'cancelled' || listItem.id === 'completed'
          const hasNotMineActiveActions = issue?.actions?.some(a => !cancelledOrCompleted(a) && getDetail(a).assignee.id !== data[page].user.id)
          const detail = issue.details.find(d => d.key === 'status')
          if (isNotActiveStatus && hasNotMineActiveActions) {
            issue.actions = changeActionsStatus(issue, listItem.id, listItem.name)
          } else if (isNotActiveStatus && !hasNotMineActiveActions) {
            detail.id = listItem.id
            detail.name = listItem.name
            issue.actions = changeActionsStatus(issue, listItem.id, listItem.name)
          } else if (!isNotActiveStatus) {
            detail.id = listItem.id
            detail.name = listItem.name
            issue.actions = changeActionsStatus(issue, listItem.id, listItem.name)
          }
        } else {
          const relationIndex = issue.details.findIndex(dv => dv.key === key)
          if (relationIndex !== -1) {
            issue.details[relationIndex] = {
              ...listItem,
              key
            }
          } else {
            issue.details.push({
              ...listItem,
              key
            })
          }
        }
        return issue
      })
      return _data
    })
  }, [ changeActionsStatus, createDescription, data, page, setPageData ])

  const actionDiff = useCallback((itemId, issueId, actionId, newAction) => {
    if (!issueId || !itemId || !actionId) {
      return
    }

    const item = findById(data[page].items, itemId)
    const issue = findById(item.issues, issueId)
    const action = findById(issue.actions, actionId)
    const { status, assignee } = getDetail(action)

    return (
      action.urgent === newAction.urgent &&
      assignee === newAction.assignee &&
      action.description === newAction.description &&
      newAction.description.length > 0 &&
      action.dueDate === newAction.dueDate &&
      status === newAction.status
    )
  }, [ data, page ])

  const editAction = useCallback((itemId, issueId, actionId, newAction) => {
    if (!actionId || !issueId || !itemId) {
      return
    }

    if (actionDiff(itemId, issueId, actionId, newAction)) {
      return
    }
    setPageData(prev => {
      const _data = { ...prev }
      const item = findById(_data[page].items, itemId)
      const issue = findById(item.issues, issueId)
      const action = findById(issue.actions, actionId)

      if (!newAction) return _data

      Object.keys(newAction)?.map(key => {
        const value = newAction[key]

        const existedRelationIndex = action.details.findIndex(
          dv => dv.key === key
        )

        const list = _data[page].lists[key]?.find(l => l.id === value)

        if (existedRelationIndex === -1) {
          action[key] = value
        } else if (existedRelationIndex !== -1) {
          action.details[existedRelationIndex].id = value
          if (key === 'status') {
            action.statusChanged = new Date().toISOString()
          }
          if (list) action.details[existedRelationIndex].name = list.name
        }
        return key
      })

      return _data
    })
  }, [ actionDiff, page, setPageData ])

  const hasActiveIssues = issues => {
    if (!issues) return false
    return issues.some(i => !cancelledOrCompleted(i))
  }

  const handleStatic = useCallback((itemId, type) => {
    setPageData(prev => {
      const _data = { ...prev }
      const item = findById(_data[page].items, itemId)
      const issueIndex = item?.issues?.findIndex(
        i => !cancelledOrCompleted(i)
      )

      if (!item.issues) item.issues = []

      if (type === 'unfavorable') {
        if (hasActiveIssues(item.issues)) {
          if (issueIndex !== -1) item.issues.splice(issueIndex, 1)
          item.answer = null
        } else {
          const newIssue = templateNewIssue({ item })
          item.issues.push(newIssue)
          item.answer = item.unfavorable
        }
        return _data
      }
      if (type === 'favorable') {
        if (issueIndex !== -1) item.issues.splice(issueIndex, 1)
        const _answer = item.unfavorable === 'no' ? 'yes' : 'no'
        item.answer = _answer === item.answer ? null : _answer
        return _data
      }
      if (type === 'delete') {
        if (issueIndex !== -1) item.issues.splice(issueIndex, 1)
        item.answer = null
        return _data
      }
      return null
    })
  }, [ page, setPageData, templateNewIssue ])

  const handleCheckbox = (id, answer) => {
    setPageData(prev => {
      const _data = { ...prev }
      const item = _data[page].items.find(t => t.id === id)
      if (item) {
        item.answer = item.answer === answer ? null : answer
      }
      return _data
    })
  }

  const handleSelectFilter = type => {
    return id => {
      const tempFilter = { ...filters }
      const existedFilter = tempFilter[type].findIndex(s => s === id)
      if (existedFilter !== -1) {
        tempFilter[type].splice(existedFilter, 1)
      } else {
        tempFilter[type].push(id)
      }
      setFilters(tempFilter)
    }
  }

  const handleAll = () => {
    const _data = _.cloneDeep(data)
    _data[page].items = _data[page].items.map(item => {
      if (
        !staticType.includes(item.type) &&
        !hasActiveIssues(item.issues) &&
        !item.hidden &&
        item.type !== 'other'
      ) {
        const newItem = { ...item }
        if (!isCheckAll) newItem.answer = favorable(newItem.unfavorable)
        else newItem.answer = null
        return newItem
      }
      return item
    })

    setCheckAll(s => !s)
    if (!_.isEqual(data, _data)) setPageData(_data)
  }

  const handleChangeResponse = (itemId, response) => {
    setPageData(prev => {
      const _data = { ...prev }
      const item = _data[page].items.find(i => i.id === itemId)
      if (item.answer === response) {
        item.answer = null
      } else {
        item.answer = response
      }
      return _data
    })
  }

  const publishable = data[page]?.draft && !draft?.publishing && !draft?.setting

  const selectedIssueNode = typeof selectedIssueId === 'string'
    ? document.getElementById(selectedIssueId)
    : null

  const selectedItem = data[page]?.items?.find(it =>
    it?.issues?.find(iss => iss.id === selectedIssueId))

  let selectedIssue = null

  if (filterItemsByStatus(selectedItem?.issues, filters).some(iss => iss.id === selectedIssueId)) {
    selectedIssue = selectedItem?.issues?.find(iss => iss.id === selectedIssueId)
  }

  const onPublish = () => {
    setLoading(true)
    publish?.()
    setLoading(false)
  }

  function DisableView () {
    if (!loading) {
      return null
    }

    return (
      <button
        onClick={e => e.stopPropagation()}
        style={{
          zIndex: 99999,
          width: '100%',
          height: '100%',
          position: 'absolute'
        }}
      />
    )
  }

  const onSort = useCallback(key => {
    const index = sort.findIndex((s) => s.key === key)
    const item = sort[index]

    setSort((prev) => [
      ...prev.slice(0, index),
      {
        ...item,
        value: item.value === 0
          ? 1
          : item.value === 1 ? -1 : 0
      },
      ...prev.slice(index + 1, prev.length)
    ])
  }, [ sort ])

  const onDiscard = () => {
    setModal({
      title:
        'Are you sure you want to cancel all changes done on this questionnaire?',
      open: true,
      actions: [
        <Button
          key={1}
          data-cy='modal_cancel-btn'
          label='Cancel'
          onClick={() => setModal(null)}
          grey
        />,
        <Button
          key={2}
          label='Discard'
          data-cy='modal_discard-btn'
          onClick={() => {
            discard()
            setModal(null)
          }}
        />
      ]
    })
  }

  const contextValue = useMemo(() => ({
    createIssue,
    editIssue,
    createAction,
    editAction,
    deleteIssue,
    filters,
    pageData: data[page],
    page,
    handleStatic,
    batch,
    sort,
    onSort,
    masterBatch,
    setMasterBatch,
    compactView,
    setCompactView,
    search,
    setSearch
  }), [ createIssue, editIssue, createAction, editAction, deleteIssue, filters, data, page, handleStatic, batch, sort, onSort, masterBatch, compactView, search ])

  return (
    <div className='flex'>
      <IGAOContext data={contextValue}>
        <PortedModal
          open={modal?.open}
          title={modal?.title}
          actions={modal?.actions}
        />
        <DisableView />
        <div className='w-full pb-[15px]'>
          {(batch.length > 0 || masterBatch) && (
            <BatchView onClick={handleBatchAction} />
          )}
          <Header
            open={open}
            setOpen={setOpen}
            handleAll={handleAll}
            switchHead={switchHead}
            setSwitchHead={setSwitchHead}
            handleSelect={handleSelectFilter}
          />

          <div className='relative'>
            <Warnings
              publish={publish}
              resolve={resolve}
              pageData={data[page]}
              collapsed={!open}
              error={draft?.error}
            >
              <Card.ExpandableWrapper
                open={open}
                setOpen={setOpen}
                style={{ padding: '0 10px' }}
              >
                <RenderItems
                  handleInputSubmit={handleInputSubmit}
                  createDescription={createDescription}
                  handleChecked={handleChecked}
                  handleBatch={handleBatch}
                  handleCheckbox={handleCheckbox}
                  handleInput={handleInput}
                  selectedIssueId={selectedIssueId}
                  setSelectedIssueId={setSelectedIssueId}
                  handleChangeResponse={handleChangeResponse}
                />
              </Card.ExpandableWrapper>
            </Warnings>

            <div className={cx(styles.bottomBar, 'sticky bottom-0 gap-4 flex flex-col')}>
              {error && (
                <Warning
                  type='concurrent'
                  title={error}
                />
              )}

              <div className={cx(styles.buttons)}>
                {![ 'tasks', 'careplan' ].includes(page) && (
                  <>
                    <DraftButton draft={draft} />
                    {publish && (
                      <PlainButton
                        disabled={!publishable}
                        style={{ margin: '0 6px' }}
                        label='Discard changes'
                        btnType='contained'
                        data-cy='discard-btn'
                        onClick={() => onDiscard()}
                      />
                    )}
                    {publish && (
                      <PlainButton
                        color='primary'
                        btnType='contained'
                        label='Publish'
                        data-cy='publish-btn'
                        disabled={!publishable || disabled}
                        onClick={() => onPublish()}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <CommentPanel
          read={readComments}
          onCreate={handleInputSubmit}
          item={selectedIssue}
          setSelectedIssueId={setSelectedIssueId}
          offset={selectedIssueNode?.offsetTop}
        />
      </IGAOContext>
    </div>
  )
}
